import { useState, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search, ArrowDownLeft, ArrowUpRight, LogIn, LogOut as LogOutIcon, Wallet, CheckCircle, Clock, XCircle, ChevronLeft, ChevronDown, X, ArrowDownUp, Trophy } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

/* ── Types ── */
type TxType = 'deposit' | 'withdraw' | 'buyin' | 'cashout' | 'commission' | 'adjustment' | 'rake' | 'jackpot'
type TxStatus = 'completed' | 'pending' | 'failed' | 'reversed'
type FilterType = 'all' | TxType

interface Transaction {
  id: string
  userId: string
  userName: string
  type: TxType
  amount: number          // cents (positive = credit, negative = debit)
  status: TxStatus
  date: Date
  reference?: string      // hand ID, withdrawal ID, etc.
  note?: string           // admin notes for adjustments
  balanceAfter: number    // cents
}

/* ── Mock Data ── */
const mockTransactions: Transaction[] = [
  { id: 'TX-10042', userId: 'U001', userName: '小明', type: 'cashout', amount: 12920, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 15), reference: 'Room D4J6', balanceAfter: 129520 },
  { id: 'TX-10041', userId: 'U001', userName: '小明', type: 'buyin', amount: -10000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 45), reference: 'Room D4J6', balanceAfter: 116600 },
  { id: 'TX-10040', userId: 'U004', userName: 'Lucky88', type: 'cashout', amount: 56000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60), reference: 'Room B2G4', balanceAfter: 368200 },
  { id: 'TX-10039', userId: 'U002', userName: 'Sarah_HK', type: 'commission', amount: 450, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 2), reference: 'Room A1F3', balanceAfter: 88950 },
  { id: 'TX-10038', userId: 'U001', userName: '小明', type: 'deposit', amount: 50000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 3), balanceAfter: 127000 },
  { id: 'TX-10037', userId: 'U005', userName: '阿花', type: 'withdraw', amount: -10000, status: 'pending', date: new Date(Date.now() - 1000 * 60 * 60 * 4), balanceAfter: 42300 },
  { id: 'TX-10036', userId: 'U004', userName: 'Lucky88', type: 'buyin', amount: -30000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 5), reference: 'Room B2G4', balanceAfter: 312200 },
  { id: 'TX-10035', userId: 'U006', userName: 'PokerKing', type: 'cashout', amount: 25000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 6), reference: 'Room B2G4', balanceAfter: 230000 },
  { id: 'TX-10034', userId: 'U001', userName: '小明', type: 'adjustment', amount: 5000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 8), note: '客服補償 - 斷線問題', balanceAfter: 77000 },
  { id: 'TX-10033', userId: 'U002', userName: 'Sarah_HK', type: 'deposit', amount: 30000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 10), balanceAfter: 88500 },
  { id: 'TX-10032', userId: 'U006', userName: 'PokerKing', type: 'commission', amount: 1200, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 12), reference: 'Room F6L8', balanceAfter: 215000 },
  { id: 'TX-10031', userId: 'U004', userName: 'Lucky88', type: 'deposit', amount: 100000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24), balanceAfter: 355200 },
  { id: 'TX-10030', userId: 'U001', userName: '小明', type: 'rake', amount: -225, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24), reference: 'H-7825', balanceAfter: 72000 },
  { id: 'TX-10029', userId: 'U005', userName: '阿花', type: 'cashout', amount: 8200, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), reference: 'Room A1F3', balanceAfter: 52300 },
  { id: 'TX-10028', userId: 'U001', userName: '小明', type: 'withdraw', amount: -25000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), balanceAfter: 72225 },
  { id: 'TX-10027', userId: 'U002', userName: 'Sarah_HK', type: 'buyin', amount: -20000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), reference: 'Room B2G4', balanceAfter: 58500 },
  { id: 'TX-10026', userId: 'U006', userName: 'PokerKing', type: 'withdraw', amount: -50000, status: 'failed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), note: '錢包地址無效', balanceAfter: 215000 },
  { id: 'TX-10025', userId: 'U004', userName: 'Lucky88', type: 'adjustment', amount: -20000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4), note: '多人桌結算修正', balanceAfter: 255200 },
  { id: 'TX-10024', userId: 'U001', userName: '小明', type: 'jackpot', amount: 250000, status: 'completed', date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), reference: 'JP-001', balanceAfter: 347225 },
]

/* ── Config ── */
const txTypeConfig: Record<TxType, { label: string; icon: typeof ArrowDownLeft; color: string; bgColor: string }> = {
  deposit:    { label: '存款',   icon: ArrowDownLeft,  color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  withdraw:   { label: '提款',   icon: ArrowUpRight,   color: 'text-muted-foreground', bgColor: 'bg-foreground/5' },
  buyin:      { label: '買入',   icon: LogIn,          color: 'text-blue-400',    bgColor: 'bg-blue-500/10' },
  cashout:    { label: '離桌',   icon: LogOutIcon,     color: 'text-cyan-400',    bgColor: 'bg-cyan-500/10' },
  commission: { label: '佣金',   icon: Wallet,         color: 'text-purple-400',  bgColor: 'bg-purple-500/10' },
  adjustment: { label: '調整',   icon: ArrowDownUp,    color: 'text-amber-400',   bgColor: 'bg-amber-500/10' },
  rake:       { label: '抽水',   icon: ArrowUpRight,   color: 'text-sky-400',     bgColor: 'bg-sky-500/10' },
  jackpot:    { label: '彩池',   icon: Trophy,         color: 'text-gold',        bgColor: 'bg-gold/10' },
}

const statusConfig: Record<TxStatus, { label: string; cls: string; icon: typeof CheckCircle }> = {
  completed: { label: '完成',   cls: 'text-emerald-400', icon: CheckCircle },
  pending:   { label: '處理中', cls: 'text-amber-400',   icon: Clock },
  failed:    { label: '失敗',   cls: 'text-rose-400',    icon: XCircle },
  reversed:  { label: '已撤銷', cls: 'text-muted-foreground', icon: XCircle },
}

const filters: { id: FilterType; label: string }[] = [
  { id: 'all', label: '全部' },
  { id: 'deposit', label: '存款' },
  { id: 'withdraw', label: '提款' },
  { id: 'buyin', label: '買入' },
  { id: 'cashout', label: '離桌' },
  { id: 'rake', label: '抽水' },
  { id: 'commission', label: '佣金' },
  { id: 'adjustment', label: '調整' },
]

const dailyVolume = [
  { day: '一', inflow: 8500, outflow: 4200 },
  { day: '二', inflow: 12000, outflow: 6800 },
  { day: '三', inflow: 6800, outflow: 3500 },
  { day: '四', inflow: 15000, outflow: 9200 },
  { day: '五', inflow: 11200, outflow: 7100 },
  { day: '六', inflow: 18500, outflow: 12400 },
  { day: '日', inflow: 14200, outflow: 8600 },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VolumeTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-surface-elevated border border-foreground/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-muted-foreground font-bold mb-1">{label}</p>
      <p className="text-emerald-400">入帳: ${(payload[0].value / 100).toFixed(0)}</p>
      <p className="text-rose-400">出帳: ${(payload[1].value / 100).toFixed(0)}</p>
    </div>
  )
}

function formatAmount(cents: number): string {
  const abs = Math.abs(cents)
  return (abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatTime(date: Date): string {
  const now = Date.now()
  const diff = now - date.getTime()
  if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))} 分鐘前`
  if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))} 小時前`
  if (diff < 1000 * 60 * 60 * 24 * 2) return '昨天 ' + date.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
}

/* ── Component ── */
export function AdminTransactionsScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const userIdParam = searchParams.get('userId')
  const userNameParam = searchParams.get('userName')

  const [search, setSearch] = useState(userIdParam ? '' : '')
  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false)
  const { headerRef, isMinimized } = useCollapsibleHeader()

  const filtered = useMemo(() => {
    return mockTransactions.filter((tx) => {
      // User filter from URL param
      if (userIdParam && tx.userId !== userIdParam) return false
      // Search
      const q = search.toLowerCase()
      if (q && !tx.id.toLowerCase().includes(q) && !tx.userName.toLowerCase().includes(q) && !tx.userId.toLowerCase().includes(q) && !(tx.reference?.toLowerCase().includes(q))) return false
      // Type filter
      if (activeFilter !== 'all' && tx.type !== activeFilter) return false
      return true
    })
  }, [search, activeFilter, userIdParam])

  const summary = useMemo(() => {
    const totalIn = filtered.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0)
    const totalOut = filtered.filter((t) => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0)
    return { count: filtered.length, totalIn, totalOut }
  }, [filtered])

  return (
    <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col min-h-full relative">
      {/* Expanded Header — scrolls away naturally */}
      <motion.div ref={headerRef as React.RefObject<HTMLDivElement>} variants={fadeInUp} className="px-4 pt-4 pb-2 space-y-3">
        {userIdParam && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/users')}
              className="w-8 h-8 rounded-lg bg-surface-elevated flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4 text-foreground" />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-sm font-bold text-foreground">{userNameParam || userIdParam}</span>
              <span className="text-[10px] font-mono text-muted-foreground px-2 py-0.5 rounded-lg bg-surface-elevated">{userIdParam}</span>
            </div>
            <button
              onClick={() => navigate('/admin/transactions')}
              className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              清除篩選
            </button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2.5 rounded-xl glass-card text-center">
            <p className="text-[10px] text-muted-foreground">交易數</p>
            <p className="text-lg font-bold font-mono text-foreground">{summary.count}</p>
          </div>
          <div className="p-2.5 rounded-xl glass-card text-center">
            <p className="text-[10px] text-emerald-400">入帳</p>
            <p className="text-lg font-bold font-mono text-emerald-400">${formatAmount(summary.totalIn)}</p>
          </div>
          <div className="p-2.5 rounded-xl glass-card text-center">
            <p className="text-[10px] text-rose-400">出帳</p>
            <p className="text-lg font-bold font-mono text-rose-400">${formatAmount(summary.totalOut)}</p>
          </div>
        </div>

        {/* 7-Day Volume Chart */}
        <div className="p-3 rounded-xl glass-card">
          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-2">7 日交易量</p>
          <ResponsiveContainer width="100%" height={80}>
            <AreaChart data={dailyVolume} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
              <defs>
                <linearGradient id="txInflow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="txOutflow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#6b7280' }} />
              <Tooltip content={<VolumeTooltip />} />
              <Area type="monotone" dataKey="inflow" stroke="#10b981" strokeWidth={2} fill="url(#txInflow)" />
              <Area type="monotone" dataKey="outflow" stroke="#f43f5e" strokeWidth={1.5} fill="url(#txOutflow)" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-[9px] text-muted-foreground">入帳</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              <span className="text-[9px] text-muted-foreground">出帳</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Minimized sticky bar — overlays content, no layout space */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -44, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -44, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-muted-foreground">{summary.count} 筆</span>
                  <span className="text-xs font-bold font-mono text-emerald-400">+${formatAmount(summary.totalIn)}</span>
                  <span className="text-xs font-bold font-mono text-rose-400">-${formatAmount(summary.totalOut)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {userIdParam && (
                    <span className="text-[10px] font-bold text-gold truncate max-w-[80px]">{userNameParam || userIdParam}</span>
                  )}
                  {/* Filter dropdown trigger */}
                  <button
                    onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all border',
                      activeFilter !== 'all'
                        ? 'bg-gold/20 text-gold border-gold/30'
                        : 'bg-surface-elevated text-muted-foreground border-foreground/5'
                    )}
                  >
                    {activeFilter === 'all' ? '篩選' : filters.find((f) => f.id === activeFilter)?.label}
                    <ChevronDown className={cn('w-3 h-3 transition-transform', filterDropdownOpen && 'rotate-180')} />
                  </button>
                </div>
              </div>

              {/* Filter dropdown menu */}
              <AnimatePresence>
                {filterDropdownOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setFilterDropdownOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-4 top-full mt-1 z-20 min-w-[140px] p-1.5 rounded-xl bg-surface-elevated border border-foreground/10 shadow-xl"
                    >
                      {filters.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => {
                            setActiveFilter(f.id)
                            setFilterDropdownOpen(false)
                          }}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-colors',
                            activeFilter === f.id
                              ? 'bg-gold/15 text-gold'
                              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                          )}
                        >
                          {f.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Search Bar */}
      <div className="px-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={userIdParam ? '搜尋交易 (ID、參考編號)' : '搜尋交易 (ID、用戶、參考編號)'}
            className="w-full h-11 pl-10 pr-4 bg-surface-elevated border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>
      </div>

      {/* Filter Chips */}
      <motion.div variants={fadeInUp} className="px-4 pb-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={cn(
                'flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-all border',
                activeFilter === f.id
                  ? 'bg-gold/20 text-gold border-gold/30'
                  : 'bg-surface-elevated text-muted-foreground border-foreground/5 hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Transaction List */}
      <motion.div variants={staggerContainer} className="px-4 pb-8 space-y-2">
        {filtered.map((tx) => {
          const cfg = txTypeConfig[tx.type]
          const StatusIcon = statusConfig[tx.status].icon
          const isPositive = tx.amount >= 0
          return (
            <motion.button
              key={tx.id}
              variants={cardReveal}
              onClick={() => setSelectedTx(tx)}
              className="w-full text-left p-3.5 rounded-2xl glass-card active:scale-[0.98] transition-transform"
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0', cfg.bgColor)}>
                  <cfg.icon className={cn('w-5 h-5', cfg.color)} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-foreground">{cfg.label}</span>
                    {!userIdParam && (
                      <span className="text-[10px] text-muted-foreground truncate">{tx.userName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-mono text-muted-foreground">{tx.id}</span>
                    {tx.reference && (
                      <span className="text-[10px] font-mono text-muted-foreground/60">{tx.reference}</span>
                    )}
                  </div>
                </div>

                {/* Amount + Status */}
                <div className="text-right flex-shrink-0">
                  <p className={cn('text-sm font-bold font-mono', isPositive ? 'text-emerald-400' : 'text-foreground')}>
                    {isPositive ? '+' : '-'}${formatAmount(tx.amount)}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <StatusIcon className={cn('w-3 h-3', statusConfig[tx.status].cls)} />
                    <span className="text-[10px] text-muted-foreground">{formatTime(tx.date)}</span>
                  </div>
                </div>
              </div>
            </motion.button>
          )
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">找不到符合條件的交易</p>
          </div>
        )}
      </motion.div>

      {/* Transaction Detail Drawer */}
      <Drawer.Root open={selectedTx !== null} onOpenChange={(open) => { if (!open) setSelectedTx(null) }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3"><div className="w-10 h-1.5 rounded-full bg-foreground/20" /></div>
              {selectedTx && (() => {
                const cfg = txTypeConfig[selectedTx.type]
                const sc = statusConfig[selectedTx.status]
                const isPositive = selectedTx.amount >= 0
                return (
                  <div className="px-6 pb-8 safe-area-bottom space-y-5">
                    {/* Header */}
                    <div className="flex items-center gap-4">
                      <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center', cfg.bgColor)}>
                        <cfg.icon className={cn('w-7 h-7', cfg.color)} />
                      </div>
                      <div className="flex-1">
                        <Drawer.Title className="text-lg font-bold text-foreground">{cfg.label}</Drawer.Title>
                        <p className="text-xs text-muted-foreground font-mono">{selectedTx.id}</p>
                      </div>
                      <div className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full border',
                        selectedTx.status === 'completed' ? 'bg-emerald-500/10 border-emerald-500/20' :
                        selectedTx.status === 'pending' ? 'bg-amber-500/10 border-amber-500/20' :
                        'bg-rose-500/10 border-rose-500/20'
                      )}>
                        <sc.icon className={cn('w-3.5 h-3.5', sc.cls)} />
                        <span className={cn('text-xs font-bold', sc.cls)}>{sc.label}</span>
                      </div>
                    </div>

                    {/* Amount display */}
                    <div className="p-5 rounded-2xl bg-surface-elevated text-center">
                      <p className={cn('text-3xl font-bold font-mono', isPositive ? 'text-emerald-400' : 'text-rose-400')}>
                        {isPositive ? '+' : '-'}${formatAmount(selectedTx.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">交易後餘額: <span className="font-mono text-foreground">${formatAmount(selectedTx.balanceAfter)}</span></p>
                    </div>

                    {/* Detail Fields */}
                    <div className="space-y-0.5">
                      {[
                        { label: '用戶', value: `${selectedTx.userName} (${selectedTx.userId})` },
                        { label: '類型', value: cfg.label },
                        { label: '時間', value: selectedTx.date.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) },
                        ...(selectedTx.reference ? [{ label: '參考編號', value: selectedTx.reference }] : []),
                        ...(selectedTx.note ? [{ label: '備註', value: selectedTx.note }] : []),
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between py-3 border-b border-foreground/5">
                          <span className="text-xs text-muted-foreground">{row.label}</span>
                          <span className="text-sm font-medium text-foreground">{row.value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Quick Actions */}
                    {!userIdParam && (
                      <button
                        onClick={() => {
                          setSelectedTx(null)
                          navigate(`/admin/transactions?userId=${selectedTx.userId}&userName=${encodeURIComponent(selectedTx.userName)}`)
                        }}
                        className="w-full h-12 rounded-2xl bg-gold/10 border border-gold/20 text-gold text-sm font-bold flex items-center justify-center gap-2"
                      >
                        查看此用戶所有交易
                      </button>
                    )}
                  </div>
                )
              })()}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
